import React, { FC, memo, useState } from "react"
import { formatDateWithDay } from "../../../../utils/date"
import { ValueOf } from "../../../../utils/type"
import Tips from "../../common/Tips"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Chip from "../../common/Chip"
import { TaskMemberCost, TaskMemberCostsMember } from "../../../../states/types/task"

type Member = TaskMemberCostsMember
type MemberCost = Omit<TaskMemberCost, 'id'>

type MemberCostRowsProps = {
  member: Member
  openMemberModalCallback: (member: Member) => void
}

const MEMBER_COST_STATUS = {
  current: { label: "現在", color: "white", backgroundColor: "#4CAF50", tintBackgroundColor: "rgba(76, 175, 80, 0.08)" },
  scheduled: { label: "予定", color: "white", backgroundColor: "#FF9800", tintBackgroundColor: "rgba(255, 152, 0, 0.08)" },
  past: { label: "過去", color: "white", backgroundColor: "#9E9E9E", tintBackgroundColor: "rgba(158, 158, 158, 0.08)" },
} as const
type MemberCostWithStatus = MemberCost & { status: ValueOf<typeof MEMBER_COST_STATUS>; isPlaceholder: boolean }

const judgeStatus = (startDate: MemberCost["period_start"], endDate: MemberCost["period_end"]): ValueOf<typeof MEMBER_COST_STATUS> => {
  const today = new Date().toISOString().slice(0, 10)
  if (endDate !== null && endDate < today) return MEMBER_COST_STATUS.past
  if (startDate !== null && startDate > today) return MEMBER_COST_STATUS.scheduled
  return MEMBER_COST_STATUS.current
}

const collapsedCosts = (memberCosts: MemberCostWithStatus[]): MemberCostWithStatus[] => {
  const current = memberCosts.find((memberCost) => memberCost.status === MEMBER_COST_STATUS.current)
  const next = memberCosts.find((memberCost) => memberCost.status === MEMBER_COST_STATUS.scheduled)
  return [current, next].filter((memberCost): memberCost is MemberCostWithStatus => memberCost !== undefined)
}

const formatApplicablePeriods = (startDate: MemberCost["period_start"], endDate: MemberCost["period_end"]): string => {
  const unset = "指定なし"
  const start = startDate ? formatDateWithDay(startDate) : unset
  const end = endDate ? formatDateWithDay(endDate) : unset
  return `${start} 〜 ${end}`
}

const MemberCostRows: FC<MemberCostRowsProps> = memo(({ member, openMemberModalCallback }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const realCostsWithStatus: MemberCostWithStatus[] = member.member_costs.map((memberCost) => ({
    ...memberCost,
    status: judgeStatus(memberCost.period_start, memberCost.period_end),
    isPlaceholder: false,
  }))
  const hasTodayCost = realCostsWithStatus.some((memberCost) => memberCost.status === MEMBER_COST_STATUS.current)
  // NOTE: 現在適用されているコスト設定がない場合、未設定であることの明示を行う
  const memberCostsWithStatus: MemberCostWithStatus[] = hasTodayCost
    ? realCostsWithStatus
    : [
      ...realCostsWithStatus.filter((memberCost) => memberCost.status === MEMBER_COST_STATUS.past),
      {
        period_start: null,
        period_end: null,
        hourly_pay: 0,
        status: MEMBER_COST_STATUS.current,
        isPlaceholder: true,
      },
      ...realCostsWithStatus.filter((memberCost) => memberCost.status === MEMBER_COST_STATUS.scheduled),
    ]
  const collapsed = collapsedCosts(memberCostsWithStatus)
  const isCollapsible = collapsed.length < memberCostsWithStatus.length
  const displayMemberCosts = isExpanded ? memberCostsWithStatus : collapsed

  return (
    <>
      {displayMemberCosts.map((memberCost, i) => {
        const rowEndClassName = i === displayMemberCosts.length - 1 ? 'row_end' : ''
        return (
          <tr key={`${memberCost.period_start ?? 'unset'}_${memberCost.isPlaceholder ? 'placeholder' : 'real'}`}>
            {i === 0 && (
              <>
                <td>
                  <Tips description="コストを変更する" className="td_container" icon={false}>
                    <FontAwesomeIcon
                      icon={['fas', 'tools']}
                      style={{ cursor: "pointer", fontSize: 26, color: "#666666" }}
                      onClick={() => openMemberModalCallback(member)}
                    />
                  </Tips>
                </td>
                <td className={`${rowEndClassName} member`}>
                  <div className="td_container">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <img
                        alt={member.name}
                        src={member.profile_image ?? `/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
                        style={{ width: "16px", height: "16px", borderRadius: "50%" }}
                      />
                      <ruby>
                        {member.name}
                        <rt>{member.kana}</rt>
                      </ruby>
                      {member.code ? ` (${member.code})` : ""}
                      {
                        member.active || (
                          <Tips
                            description="休眠中💤"
                            className=""
                            icon={false}
                            style={{
                              position: "relative",
                            }}
                          >
                            <>
                              💤
                            </>
                          </Tips>
                        )
                      }
                    </div>
                  </div>
                </td>
              </>
            )}
            {i !== 0 && (
              <>
                <td />
                <td className={`${rowEndClassName} member`} />
              </>
            )}
            <td className={rowEndClassName} style={{ backgroundColor: memberCost.status.tintBackgroundColor }}>
              <div className="td_container">
                <Chip
                  label={memberCost.status.label}
                  color={memberCost.status.color}
                  backgroundColor={memberCost.status.backgroundColor}
                />
              </div>
            </td>
            <td className={rowEndClassName} style={{ backgroundColor: memberCost.status.tintBackgroundColor }}>
              <div className="td_container">
                {memberCost.isPlaceholder ? "未設定" : formatApplicablePeriods(memberCost.period_start, memberCost.period_end)}
              </div>
            </td>
            <td className={rowEndClassName} style={{ backgroundColor: memberCost.status.tintBackgroundColor }}>
              <div className="td_container">{`¥${memberCost.hourly_pay.toLocaleString()}`}</div>
            </td>
            {i === 0 && (
              <td className={rowEndClassName}>
                <div className="td_container">
                  <Tips
                    description="全て表示中"
                    className="td_container expand"
                    icon={false}
                    hideTips={isCollapsible}
                  >
                    <button
                      className="expand_button"
                      type="button"
                      onClick={() => setIsExpanded(!isExpanded)}
                      disabled={!isCollapsible}
                    >
                      {isExpanded ? "閉じる" : "全部見る"}
                    </button>
                  </Tips>
                </div>
              </td>
            )}
            {i !== 0 && (
              <td className={rowEndClassName} />
            )}
          </tr>
        )
      })}
    </>
  )
})

export default MemberCostRows
